import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Fingerprint, ShieldCheck, ShieldAlert, RefreshCw, Loader2, Search, Link2 } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";

const ACTION_LABELS = {
  "product.delete": "Ürün silme", "category.delete": "Kategori silme", "customer.delete": "Müşteri silme",
  "document.delete": "Belge silme", "finance.delete": "Muhasebe kaydı silme", "sale.cancel": "Satış / işlem iptali",
  "user.create": "Kullanıcı ekleme", "user.update": "Kullanıcı değişikliği", "user.perm.override": "Kişiye özel yetki değişikliği",
  "system.reset": "Sistem sıfırlama", "role.perms": "Rol yetki değişikliği", "role.superiors": "Rol amir değişikliği",
  "role.create": "Rol oluşturma", "role.update": "Rol düzenleme", "role.delete": "Rol silme", "role.active": "Rol aktif/pasif",
};
const shortHash = (h) => (h && h !== "GENESIS" ? h.slice(0, 10) + "…" : (h || "—"));
const dt = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); } catch { return iso; } };

export default function SecurityLedger() {
  const [rows, setRows] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, v] = await Promise.all([
        api.get("/security/ledger", { params: { limit: 1000 } }),
        api.get("/security/ledger/verify"),
      ]);
      setRows(l.data || []);
      setIntegrity(v.data || null);
    } catch (e) {
      toast.error("Güvenlik defteri yüklenemedi (yalnız Yönetici görüntüler).");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const verifyNow = async () => {
    setVerifying(true);
    try {
      const { data } = await api.get("/security/ledger/verify");
      setIntegrity(data);
      toast[data.intact ? "success" : "error"](data.intact ? "Defter bütünlüğü DOĞRULANDI — hiçbir kayıt değiştirilmemiş." : "UYARI: Defterde bütünlük ihlali tespit edildi!");
    } catch { toast.error("Doğrulama başarısız"); }
    finally { setVerifying(false); }
  };

  const ql = q.trim().toLocaleLowerCase("tr-TR");
  const filtered = ql ? rows.filter((r) => [r.actor, r.detail, r.action_type, ACTION_LABELS[r.action_type], r.actor_role].some((x) => String(x || "").toLocaleLowerCase("tr-TR").includes(ql))) : rows;
  const paged = usePaged(filtered, 20);
  const intact = integrity?.intact;

  return (
    <div className="space-y-4" data-testid="security-ledger-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900"><Fingerprint className="h-5 w-5 text-emerald-400" /></div>
          <div>
            <p className="overline text-slate-400">Yönetim · Güvenlik</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Güvenlik Gözü (Defter)</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="sl-refresh" variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile</Button>
          <Button data-testid="sl-verify-btn" size="sm" onClick={verifyNow} disabled={verifying} className="bg-slate-900 text-white hover:bg-slate-800">{verifying ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />} Bütünlüğü Doğrula</Button>
        </div>
      </div>

      <div data-testid="sl-integrity-badge" className={`flex flex-wrap items-center gap-3 rounded-xl border-2 p-4 ${intact == null ? "border-slate-200 bg-white" : intact ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
        {intact == null ? <ShieldCheck className="h-8 w-8 text-slate-300" /> : intact ? <ShieldCheck className="h-8 w-8 text-emerald-600" /> : <ShieldAlert className="h-8 w-8 text-red-600" />}
        <div className="flex-1">
          <p className={`font-heading text-lg font-bold ${intact == null ? "text-slate-500" : intact ? "text-emerald-700" : "text-red-700"}`}>
            {intact == null ? "Bütünlük durumu bekleniyor…" : intact ? "Defter Bütünlüğü SAĞLAM" : "BÜTÜNLÜK İHLALİ TESPİT EDİLDİ"}
          </p>
          <p className="text-sm text-slate-500">
            {integrity ? `${integrity.entries} kayıt · hash-zincirli, değiştirilemez.` : "Tüm yıkıcı/güvenlik işlemleri buraya kalıcı yazılır; app üzerinden silinemez/düzenlenemez."}
            {integrity && !intact && integrity.broken ? ` — İhlal: #${integrity.broken.at_seq} (${integrity.broken.reason})` : ""}
          </p>
        </div>
        <Link2 className="h-5 w-5 text-slate-300" />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input data-testid="sl-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kişi, işlem veya kayıt ara…" className="pl-8" />
      </div>

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-3 py-3">#</th><th className="px-3 py-3">Tarih</th><th className="px-3 py-3">İşlem</th>
              <th className="px-3 py-3">Sonuç</th><th className="px-3 py-3">Kişi</th><th className="px-3 py-3">Kayıt / Hedef</th><th className="px-3 py-3">Hash</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Yükleniyor…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} data-testid="sl-empty" className="px-4 py-10 text-center text-slate-400">Defterde kayıt yok. Yıkıcı/güvenlik işlemleri yapıldıkça burada görünür.</td></tr>}
            {paged.slice.map((r) => (
              <tr key={r.id || r.seq} data-testid={`sl-row-${r.seq}`} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 font-mono text-xs text-slate-400">{r.seq}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{dt(r.at)}</td>
                <td className="px-3 py-2.5"><span className="font-medium text-slate-800">{ACTION_LABELS[r.action_type] || r.action_type}</span></td>
                <td className="px-3 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${String(r.event).includes("reject") ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>{r.event}</span></td>
                <td className="px-3 py-2.5"><span className="text-slate-700">{r.actor}</span>{r.actor_role && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{r.actor_role}</span>}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.detail || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-[10px] text-slate-400" title={`hash: ${r.hash}\nönceki: ${r.prev_hash}`}>{shortHash(r.hash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onPage={paged.setPage} onPageSize={paged.setPageSize} testid="security-ledger-pagination" />
    </div>
  );
}
